import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios_instance from '../api/axios_instance';
import { API_URL } from '../config';
import { 
  Loader2, Edit, Archive, Plus, Layers, 
  FolderTree, X, Upload, Save, RefreshCw,
  CheckCircle2, AlertCircle, Trash2, AlertTriangle
} from 'lucide-react';

const ManageCategories = () => {
  const navigate = useNavigate(); // تعريف تابع التنقل
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [preview, setPreview] = useState(null);
  
  // حالة التحكم في مودال التأكيد المخصص
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const initialDataRef = useRef(null);
  const backendURL = API_URL;

  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCategories = async () => {
    try {
      const res = await axios_instance.get('/api/categories');
      setCategories(res.data);
    } catch (err) { 
      showToast("فشل في جلب الأقسام", "error");
    } finally { 
      setLoading(false); 
    }
  };

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const toggleStatus = async (id, currentIsArchived) => {
    try {
      await axios_instance.patch(`/api/categories/${id}/archive`, { 
        isArchived: !currentIsArchived 
      });
      showToast(!currentIsArchived ? "تم أرشفة القسم بنجاح" : "تم استعادة القسم بنجاح");
      fetchCategories();
    } catch (err) { 
      showToast("فشلت عملية تغيير الحالة", "error"); 
    }
  };

  const handleEditClick = (cat) => {
    const data = {
      _id: cat._id,
      nameAr: cat.name?.ar || '',
      nameEn: cat.name?.en || '',
      nameFr: cat.name?.fr || '',
      parentCategory: cat.parentCategory?._id || cat.parentCategory || '',
      image: cat.image,
      newImage: null
    };
    setEditingCategory(data);
    initialDataRef.current = JSON.stringify(data);
    setPreview(cat.image ? `${backendURL}/${cat.image}` : null);
  };

  const executeDeleteImage = async () => {
    try {
      await axios_instance.delete(`/api/categories/${editingCategory._id}/image`);
      setPreview(null);
      setEditingCategory({ ...editingCategory, image: null, newImage: null });
      fetchCategories();
      showToast("تم حذف الصورة من الخادم بنجاح");
    } catch (err) {
      showToast("فشل حذف الصورة", "error");
    } finally {
      setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
    }
  };

  const triggerDeleteConfirm = () => {
    setConfirmModal({
      show: true,
      title: 'حذف الصورة',
      message: 'هل أنت متأكد من حذف هذه الصورة نهائياً من الخادم؟ لا يمكن التراجع عن هذا الإجراء.',
      onConfirm: executeDeleteImage
    });
  };

  const hasChanges = useMemo(() => {
    if (!editingCategory || !initialDataRef.current) return false;
    const currentData = JSON.stringify({
        ...editingCategory,
        newImage: editingCategory.newImage ? 'changed' : null
    });
    const parsedInitial = JSON.parse(initialDataRef.current);
    const initialCompare = JSON.stringify({ ...parsedInitial, newImage: null });
    return currentData !== initialCompare;
  }, [editingCategory]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;
    setUpdating(true);
    
    const data = new FormData();
    const initial = JSON.parse(initialDataRef.current);

    if (editingCategory.nameAr !== initial.nameAr) data.append('name.ar', editingCategory.nameAr);
    if (editingCategory.nameEn !== initial.nameEn) data.append('name.en', editingCategory.nameEn);
    if (editingCategory.nameFr !== initial.nameFr) data.append('name.fr', editingCategory.nameFr);
    
    if (editingCategory.parentCategory !== initial.parentCategory) {
      data.append('parentCategory', editingCategory.parentCategory || "");
    }
    
    if (editingCategory.newImage) {
      data.append('image', editingCategory.newImage);
    }

    try {
      await axios_instance.put(`/api/categories/${editingCategory._id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast("تم التحديث بنجاح");
      setEditingCategory(null);
      fetchCategories();
    } catch (err) { 
      showToast("فشل التحديث", "error"); 
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20 bg-gray-50 min-h-screen">
      <Loader2 className="animate-spin text-emerald-600" size={40} />
    </div>
  );

  return (
    <div className="space-y-8 relative min-h-screen" dir="rtl">
      
      {/* التنبيهات (Toast) */}
      {notification.show && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 px-6 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-10 duration-300 bg-white ${
          notification.type === 'success' ? 'border-emerald-100 text-emerald-700' : 'border-red-100 text-red-700'
        }`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className="text-red-500" />}
          <span className="text-sm font-bold">{notification.message}</span>
          <button onClick={() => setNotification({ ...notification, show: false })} className="mr-4 text-gray-400"><X size={16} /></button>
        </div>
      )}

      {/* مودال التأكيد المخصص */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setConfirmModal({...confirmModal, show: false})}></div>
          <div className="relative bg-white w-full max-w-md rounded-3xl border border-gray-200 p-8 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="p-4 bg-red-50 text-red-500 rounded-2xl">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-black text-gray-900">{confirmModal.title}</h3>
              <p className="text-gray-500 font-bold text-sm leading-relaxed">{confirmModal.message}</p>
              <div className="flex gap-3 w-full mt-4">
                <button 
                  onClick={confirmModal.onConfirm}
                  className="flex-1 bg-red-500 text-white py-3 rounded-xl font-black hover:bg-red-600 transition-colors"
                >
                  تأكيد الحذف
                </button>
                <button 
                  onClick={() => setConfirmModal({...confirmModal, show: false})}
                  className="flex-1 bg-gray-100 text-gray-600 py-3 rounded-xl font-black hover:bg-gray-200 transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">إدارة الأقسام</h2>
          <p className="text-gray-500 font-bold mt-1 text-sm uppercase">إجمالي الأقسام: {categories.length}</p>
        </div>
        <button
          onClick={() => navigate('/admin/addCategory')}
          className="bg-emerald-600 text-white px-5 py-3 md:px-8 md:py-4 rounded-2xl font-black flex items-center gap-2 hover:bg-emerald-700 transition-colors shrink-0"
        >
          <Plus size={20} /> <span className="hidden sm:inline">إضافة قسم جديد</span><span className="sm:hidden">إضافة</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-right border-collapse min-w-[500px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-wider">القسم</th>
              <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-wider">النوع</th>
              <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-wider text-center">الحالة</th>
              <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-wider text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map((cat) => (
              <tr key={cat._id} className="hover:bg-gray-50/50 transition-colors">
                <td className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-gray-200 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                      {cat.image ? <img src={`${backendURL}/${cat.image}`} className="w-full h-full object-contain p-1" alt="" /> : <Layers className="text-gray-200" />}
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-sm">{cat.name.ar}</p>
                      <p className="text-[10px] text-gray-400 font-bold uppercase">{cat.name.en}</p>
                    </div>
                  </div>
                </td>
                <td className="p-5 text-[10px] font-black uppercase">
                  {cat.parentCategory ? (
                    <span className="text-blue-600 bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-lg flex items-center gap-1 w-fit">
                      <FolderTree size={12} /> فرعي: {cat.parentCategory.name?.ar}
                    </span>
                  ) : (
                    <span className="text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-lg w-fit">رئيسي</span>
                  )}
                </td>
                <td className="p-5 text-center">
                  <span className={`px-3 py-1 rounded-lg text-[10px] font-black border ${!cat.isArchived ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                    {!cat.isArchived ? 'نشط' : 'مؤرشف'}
                  </span>
                </td>
                <td className="p-5 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => handleEditClick(cat)} className="p-2.5 border border-gray-200 rounded-xl hover:bg-white text-gray-600"><Edit size={18} /></button>
                    <button onClick={() => toggleStatus(cat._id, cat.isArchived)} className={`p-2.5 border border-gray-200 rounded-xl hover:bg-white transition-colors ${!cat.isArchived ? 'text-amber-500' : 'text-emerald-500'}`}>
                      {!cat.isArchived ? <Archive size={18} /> : <RefreshCw size={18} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Edit Drawer Modal */}
      {editingCategory && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={() => setEditingCategory(null)}></div>
          <div className="relative bg-white w-full max-w-2xl rounded-3xl border border-gray-200 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-600 text-white rounded-xl"><Edit size={20} /></div>
                <h3 className="text-xl font-black text-gray-900 tracking-tight">تعديل القسم</h3>
              </div>
              <button onClick={() => setEditingCategory(null)} className="p-2 hover:bg-gray-200 rounded-full text-gray-400"><X size={20} /></button>
            </div>

            <form onSubmit={handleUpdate} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-gray-500 uppercase">الاسم (العربية)</label>
                  <input type="text" value={editingCategory.nameAr} onChange={(e) => setEditingCategory({...editingCategory, nameAr: e.target.value})} className="w-full p-3 border border-gray-300 rounded-xl outline-none text-sm font-bold focus:border-emerald-500" required />
                </div>
                <div className="space-y-1.5" dir="ltr">
                  <label className="text-[11px] font-black text-gray-500 uppercase">Name (EN)</label>
                  <input type="text" value={editingCategory.nameEn} onChange={(e) => setEditingCategory({...editingCategory, nameEn: e.target.value})} className="w-full p-3 border border-gray-300 rounded-xl outline-none text-sm font-bold focus:border-emerald-500 text-left" required />
                </div>
                <div className="space-y-1.5" dir="ltr">
                  <label className="text-[11px] font-black text-gray-500 uppercase">Nom (FR)</label>
                  <input type="text" value={editingCategory.nameFr} onChange={(e) => setEditingCategory({...editingCategory, nameFr: e.target.value})} className="w-full p-3 border border-gray-300 rounded-xl outline-none text-sm font-bold focus:border-emerald-500 text-left" required />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
                <div className="space-y-4">
                    <label className="text-[11px] font-black text-gray-500 uppercase block">أيقونة القسم</label>
                    <div className="flex items-center gap-4">
                        <label className={`cursor-pointer relative flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-2xl bg-gray-50 transition-all ${preview ? 'border-emerald-200' : 'border-gray-200 hover:border-emerald-400'}`}>
                            {preview ? <img src={preview} className="w-full h-full object-contain p-2" alt="" /> : (
                                <div className="text-center">
                                    <Upload className="text-gray-300 mx-auto mb-1" size={24} />
                                    <span className="text-[9px] font-black text-gray-400 uppercase">رفع ملف</span>
                                </div>
                            )}
                            <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                const file = e.target.files[0];
                                if(file) {
                                    setEditingCategory({...editingCategory, newImage: file});
                                    setPreview(URL.createObjectURL(file));
                                }
                            }} />
                        </label>
                        {preview && (
                            <button type="button" onClick={triggerDeleteConfirm} className="p-3 bg-red-50 text-red-500 rounded-xl border border-red-100 hover:bg-red-100 transition-colors">
                                <Trash2 size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-gray-500 uppercase">القسم الأب</label>
                    <select value={editingCategory.parentCategory} onChange={(e) => setEditingCategory({...editingCategory, parentCategory: e.target.value})} className="w-full p-3 border border-gray-300 rounded-xl bg-white text-sm font-bold">
                        <option value="">-- رئيسي --</option>
                        {categories.filter(c => !c.parentCategory && c._id !== editingCategory._id).map(p => (
                            <option key={p._id} value={p._id}>{p.name.ar}</option>
                        ))}
                    </select>
                </div>
              </div>

              <button type="submit" disabled={updating || !hasChanges} className={`w-full py-4 rounded-2xl font-black text-sm uppercase flex items-center justify-center gap-3 transition-all border ${hasChanges && !updating ? 'bg-gray-900 text-white border-gray-900 hover:bg-black' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                {updating ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
                {hasChanges ? 'حفظ التعديلات' : 'لا توجد تعديلات'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCategories;